import { computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal, Signal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { isEqual } from 'es-toolkit';
import type { AggregatedRiskWidgetConfig } from 'src/features/aggregated-risk/config';
import type { GridSettingsNavigationTarget } from 'src/features/grid-settings/models/NavigationModel';
import { ensureColumnBySlugWithName } from 'src/features/table/lib/ensureColumns';
import { StickyFilterModel } from 'src/models/StickyFilterModel';
import type { StickyFilterModelEventBus, StickyFilterState } from 'src/models/StickyFilterModel';
import type { TableModel } from 'src/models/TableModel';
import { getColumnTemplateByIdQuery, getDefaultColumnTemplateQuery } from 'src/queries/columnTemplates';
import { generateWorkableColumnTemplateGridSettings } from 'src/routes/dashboard/widget-container/widget-settings-editor/context';
import type { WidgetHostContainer } from 'src/services/WorkspaceService/Container/Container';
import { ContainerChannelSlice } from 'src/services/WorkspaceService/Container/slices/ContainerChannelSlice';
import type { ContainerChannelSliceEventBus } from 'src/services/WorkspaceService/Container/slices/ContainerChannelSlice';
import { ContainerTableSlice } from 'src/services/WorkspaceService/Container/slices/ContainerTableSlice';
import type { ContainerTableSliceEventBus } from 'src/services/WorkspaceService/Container/slices/ContainerTableSlice';
import { ContainerWidgetTabSlice } from 'src/services/WorkspaceService/Container/slices/ContainerWidgetTabSlice';
import type { ContainerWidgetTabSliceEventBus } from 'src/services/WorkspaceService/Container/slices/ContainerWidgetTabSlice';
import type { WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { ReactBindings } from 'src/types/bindings';
import type {
    GridSettingsDialogOpenEvent,
    GridSettingsDialogTargetSignal,
    IsColumnTemplateLibraryOpenSignal,
    IsGridSettingsDialogOpenSignal,
    OpenColumnTemplateLibraryEvent,
    SelectColumnTemplateEvent,
    SelectedColumnTemplateSignal,
    StickyFilterSliceEvents,
    StickyFilterSliceSignals,
    UpdateConcreteTemplateEvent,
} from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import type { EventBus, EventBusEvents, EventBusSignals } from 'src/types/EventBus/EventBus';

export interface AggregatedRiskWidgetState extends StickyFilterState {
    columnTemplate: ColumnTemplate | string | null;
}

export type AggregatedRiskColumnTemplateSignals = SelectedColumnTemplateSignal &
    IsColumnTemplateLibraryOpenSignal &
    IsGridSettingsDialogOpenSignal &
    GridSettingsDialogTargetSignal;

export type AggregatedRiskColumnTemplateEvents = SelectColumnTemplateEvent &
    UpdateConcreteTemplateEvent &
    OpenColumnTemplateLibraryEvent &
    GridSettingsDialogOpenEvent;

export type AggregatedRiskSelfOwnedEventBus = EventBus<
    EventBusSignals<ContainerChannelSliceEventBus> &
        EventBusSignals<ContainerWidgetTabSliceEventBus> &
        EventBusSignals<ContainerTableSliceEventBus> &
        StickyFilterSliceSignals &
        AggregatedRiskColumnTemplateSignals,
    EventBusEvents<ContainerChannelSliceEventBus> &
        EventBusEvents<ContainerWidgetTabSliceEventBus> &
        EventBusEvents<ContainerTableSliceEventBus> &
        StickyFilterSliceEvents &
        AggregatedRiskColumnTemplateEvents
>;

export interface AggregatedRiskSelfOwnedModelInit {
    config: AggregatedRiskWidgetConfig;
    container: WidgetHostContainer;
    logger: Logger;
    persisted: AggregatedRiskWidgetState;
    queryClient: ReactBindings['QueryClient'];
    slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    tabId: WidgetId;
    table: TableModel;
}

export interface AggregatedRiskSelfOwnedModelShape {
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: AggregatedRiskSelfOwnedEventBus;
    readonly columnTemplate: ReadonlySignal<ColumnTemplate | null>;
    readonly groupByIndicatorId: ReadonlySignal<string | null>;
    readonly columnTemplateError: ReadonlySignal<Error | null>;
    toJSON(): AggregatedRiskWidgetState;
    markClean(): void;
}

function asError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
}

async function ensureGroupByColumn({
    columnTemplate,
    config,
    slugMapServiceProvider,
}: {
    columnTemplate: ColumnTemplate;
    config: AggregatedRiskWidgetConfig;
    slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
}): Promise<{ columnTemplate: ColumnTemplate; groupByIndicatorId: string }> {
    const slugMapService = await slugMapServiceProvider();
    const indicator = slugMapService.slugMap.value[config.groupBySlug];

    if (!indicator) {
        throw new Error(`Missing required group-by indicator for slug ${config.groupBySlug}`);
    }
    if (indicator.disabled) {
        throw new Error(`Required group-by indicator for slug ${config.groupBySlug} is disabled`);
    }

    const found = columnTemplate.columns.some((column) => {
        const isGroupByColumn =
            column.id === indicator.id || (column.type === 'indicator' && column.indicator.id === indicator.id);

        return isGroupByColumn;
    });

    const columns = found
        ? columnTemplate.columns
        : ensureColumnBySlugWithName(slugMapService.slugMap.value, columnTemplate.columns, {
              name: indicator.name,
              slug: config.groupBySlug,
          });

    return {
        columnTemplate: {
            ...columnTemplate,
            columns,
        },
        groupByIndicatorId: indicator.id,
    };
}

export const AggregatedRiskSelfOwnedModel: ModelConstructor<
    AggregatedRiskSelfOwnedModelShape,
    [AggregatedRiskSelfOwnedModelInit]
> = createModel<AggregatedRiskSelfOwnedModelShape, [AggregatedRiskSelfOwnedModelInit]>(
    (init: AggregatedRiskSelfOwnedModelInit) => {
        const channelSlice = new ContainerChannelSlice({ container: init.container, tabId: init.tabId });
        const widgetTabSlice = new ContainerWidgetTabSlice({
            container: init.container,
            tabId: init.tabId,
            logger: init.logger,
        });
        const tableSlice = new ContainerTableSlice({ table: init.table });
        const stickyFilterModel = new StickyFilterModel({ persisted: init.persisted });

        const persistedColumnTemplate: Signal<ColumnTemplate | string | null> = signal(init.persisted.columnTemplate);
        const cleanColumnTemplate: Signal<ColumnTemplate | string | null> = signal(init.persisted.columnTemplate);
        const resolvedColumnTemplate: Signal<ColumnTemplate | null> = signal(null);
        const groupByIndicatorId: Signal<string | null> = signal(null);
        const columnTemplateError: Signal<Error | null> = signal(null);
        const isGridSettingsDialogOpen = signal(false);
        const gridSettingsDialogTarget = signal<GridSettingsNavigationTarget | null>(null);
        const isColumnTemplateLibraryOpen = signal(false);
        const dirty: ReadonlySignal<boolean> = computed(
            () => !isEqual(persistedColumnTemplate.value, cleanColumnTemplate.value) || stickyFilterModel.dirty.value,
        );
        let resolveRequestId = 0;

        const openGridSettingsDialog = (payload: {
            isOpen: boolean;
            targetTab?: GridSettingsNavigationTarget['tab'];
            targetColumnId?: string;
        }) => {
            isGridSettingsDialogOpen.value = payload.isOpen;
            gridSettingsDialogTarget.value =
                payload.isOpen && (payload.targetTab !== undefined || payload.targetColumnId !== undefined)
                    ? { tab: payload.targetTab, columnId: payload.targetColumnId }
                    : null;
        };

        const resolveColumnTemplate = async (incomingColumnTemplate: ColumnTemplate | string | null) => {
            const requestId = ++resolveRequestId;
            columnTemplateError.value = null;
            resolvedColumnTemplate.value = null;
            groupByIndicatorId.value = null;

            try {
                const columnTemplate =
                    incomingColumnTemplate === null
                        ? await init.queryClient.fetchQuery(getDefaultColumnTemplateQuery(init.config.templateKey))
                        : typeof incomingColumnTemplate === 'string'
                          ? await init.queryClient.fetchQuery(getColumnTemplateByIdQuery(incomingColumnTemplate))
                          : incomingColumnTemplate;

                const ensured = await ensureGroupByColumn({
                    columnTemplate,
                    config: init.config,
                    slugMapServiceProvider: init.slugMapServiceProvider,
                });

                if (requestId !== resolveRequestId) {
                    return;
                }

                groupByIndicatorId.value = ensured.groupByIndicatorId;
                resolvedColumnTemplate.value = generateWorkableColumnTemplateGridSettings(ensured.columnTemplate);
            } catch (error) {
                if (requestId !== resolveRequestId) {
                    return;
                }

                const normalizedError = asError(error);
                init.logger.error('Failed to resolve aggregated risk column template', {
                    error: normalizedError,
                    incomingColumnTemplate,
                    tableKind: init.config.tableKind,
                    templateKey: init.config.templateKey,
                });
                resolvedColumnTemplate.value = null;
                groupByIndicatorId.value = null;
                columnTemplateError.value = normalizedError;
            }
        };

        const setColumnTemplate = (columnTemplate: ColumnTemplate | string | null) => {
            if (
                isEqual(persistedColumnTemplate.value, columnTemplate) &&
                columnTemplateError.value === null &&
                resolvedColumnTemplate.value !== null
            ) {
                return;
            }

            persistedColumnTemplate.value = columnTemplate;
            void resolveColumnTemplate(columnTemplate);
        };

        const columnTemplateEventBus = EventBusBuilder.create()
            .addEvent('widget::updateConcreteTemplate', (payload) => {
                setColumnTemplate(payload.columnTemplate);
            })
            .addEvent('widget::selectColumnTemplate', (payload) => {
                setColumnTemplate(payload.templateId);
            })
            .addEvent('widget::onColumnTemplateLibraryOpen', (payload) => {
                isColumnTemplateLibraryOpen.value = payload.isOpen;
            })
            .addEvent('widget::openGridSettingsDialog', openGridSettingsDialog)
            .addSignal('widget::selectedColumnTemplate', () => persistedColumnTemplate)
            .addSignal('widget::isColumnTemplateLibraryOpen', () => isColumnTemplateLibraryOpen)
            .addSignal('widget::isGridSettingsDialogOpen', () => isGridSettingsDialogOpen)
            .addSignal('widget::gridSettingsDialogTarget', () => gridSettingsDialogTarget)
            .build();

        const eventBus: AggregatedRiskSelfOwnedEventBus = MergedEventBus.from<ContainerChannelSliceEventBus>(
            channelSlice.eventBus,
        )
            .merge<ContainerWidgetTabSliceEventBus>(widgetTabSlice.eventBus)
            .merge<ContainerTableSliceEventBus>(tableSlice.eventBus)
            .merge<StickyFilterModelEventBus>(stickyFilterModel.eventBus)
            .merge(columnTemplateEventBus);

        void resolveColumnTemplate(persistedColumnTemplate.value);

        return {
            dirty,
            eventBus,
            columnTemplate: resolvedColumnTemplate as ReadonlySignal<ColumnTemplate | null>,
            groupByIndicatorId: groupByIndicatorId as ReadonlySignal<string | null>,
            columnTemplateError: columnTemplateError as ReadonlySignal<Error | null>,
            toJSON(): AggregatedRiskWidgetState {
                return {
                    ...init.persisted,
                    columnTemplate: persistedColumnTemplate.value,
                    ...stickyFilterModel.toJSON(),
                };
            },
            markClean(): void {
                cleanColumnTemplate.value = persistedColumnTemplate.value;
                stickyFilterModel.markClean();
            },
        };
    },
);

export type AggregatedRiskSelfOwnedModel = Model<AggregatedRiskSelfOwnedModelShape>;
